import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, Wrench } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import { serviceJobsApi } from '@/services/api';
import type { POSServiceJob } from '@/types';
import { formatMoney } from '@/utils/currency';

export default function ServiceJobsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [incompleteOnly, setIncompleteOnly] = useState(true);
  const [status, setStatus] = useState('');

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['allServiceJobs', incompleteOnly, status],
    queryFn: async () => {
      const res = await serviceJobsApi.listJobs({
        incomplete: incompleteOnly ? 1 : 0,
        status: status || undefined,
        limit: 300,
      });
      return (res.data.message || []) as POSServiceJob[];
    },
  });

  const { data: staff = [] } = useQuery({
    queryKey: ['serviceStaff'],
    queryFn: async () => {
      const res = await serviceJobsApi.listStaff();
      return (res.data.message || []) as { user: string; full_name: string }[];
    },
  });

  const assignMut = useMutation({
    mutationFn: ({ name, assigned_to }: { name: string; assigned_to: string }) =>
      serviceJobsApi.assign(name, assigned_to),
    onSuccess: () => {
      toast.success(t('services.assigned', 'Assigned'));
      void qc.invalidateQueries({ queryKey: ['allServiceJobs'] });
    },
  });

  const completeMut = useMutation({
    mutationFn: (name: string) => serviceJobsApi.updateStatus(name, 'Completed'),
    onSuccess: () => {
      toast.success(t('services.markedComplete', 'Marked complete'));
      void qc.invalidateQueries({ queryKey: ['allServiceJobs'] });
    },
  });

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TrialBanner />
        <div className="p-4 sm:p-6 border-b border-gray-200 bg-white flex flex-wrap items-center gap-3 justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary-600" />
              {t('services.jobsBoard', 'Service Jobs')}
            </h1>
            <p className="text-sm text-gray-500">
              {t('services.jobsBoardSub', 'Who did what — pending and completed services')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <input
                type="checkbox"
                checked={incompleteOnly}
                onChange={(e) => {
                  setIncompleteOnly(e.target.checked);
                  if (e.target.checked) setStatus('');
                }}
              />
              {t('services.incompleteOnly', 'Incomplete only')}
            </label>
            <select
              className="input-field w-auto"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                if (e.target.value) setIncompleteOnly(false);
              }}
            >
              <option value="">{t('common.all', 'All')}</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('services.service', 'Service')}</th>
                    <th className="px-3 py-2 font-medium">{t('common.customer', 'Customer')}</th>
                    <th className="px-3 py-2 font-medium">{t('services.staff', 'Staff')}</th>
                    <th className="px-3 py-2 font-medium">{t('common.status', 'Status')}</th>
                    <th className="px-3 py-2 font-medium">{t('services.when', 'When')}</th>
                    <th className="px-3 py-2 font-medium">{t('common.amount', 'Amount')}</th>
                    <th className="px-3 py-2 font-medium">{t('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job.name} className="border-t border-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">{job.item_name}</td>
                      <td className="px-3 py-2 text-gray-600">{job.customer_name || '—'}</td>
                      <td className="px-3 py-2">
                        <select
                          className="input-field py-1 text-xs"
                          value={job.assigned_to || ''}
                          onChange={(e) => assignMut.mutate({ name: job.name, assigned_to: e.target.value })}
                        >
                          <option value="">{t('services.unassigned', 'Unassigned')}</option>
                          {staff.map((s) => (
                            <option key={s.user} value={s.user}>
                              {s.full_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">{job.status}</td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                        {job.scheduled_at
                          ? new Date(job.scheduled_at).toLocaleString()
                          : t('services.walkIn', 'Walk-in')}
                      </td>
                      <td className="px-3 py-2 font-semibold">{formatMoney(job.amount || 0)}</td>
                      <td className="px-3 py-2">
                        {(job.status === 'Pending' || job.status === 'In Progress') && (
                          <button
                            type="button"
                            className="text-primary-700 font-medium hover:underline"
                            onClick={() => completeMut.mutate(job.name)}
                          >
                            {t('services.complete', 'Complete')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {jobs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-10 text-center text-gray-400">
                        {t('services.noJobs', 'No jobs here')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
